// Export modal — replicates the v5 mockup at access-preview-v5.html
// lines 3010–3052.
//
// Submits POST /api/admin/audit/exports with the current page filters or
// an empty filter spec ("All events"). The server enforces the 30-day cap
// and returns 202 {export_id, status:"queued"}.
//
// On success, navigates to /admin/audit/exports so the operator can watch
// the row transition queued → running → ready.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import { Button, Modal, useToast } from "@/components";
import {
  auditClient,
  type AuditExportFilterSpec,
  type AuditSubscriptionFilterSpec,
} from "@/data/clients/auditClient";
import "./Audit.css";

export interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the new export id once the row is queued. */
  onQueued: (exportId: string) => void;
  /** Filter spec to ship with "Current filtered view" — same shape as the
   *  subscriptions page uses; from/to are required. */
  currentFilterSpec: AuditSubscriptionFilterSpec;
}

type IncludeChoice = "filtered" | "all";

function describeFilters(spec: AuditExportFilterSpec): string {
  const bits: string[] = [];
  if (spec.locations && spec.locations.length > 0) {
    bits.push(`${spec.locations.length} location${spec.locations.length === 1 ? "" : "s"}`);
  }
  if (spec.event_types && spec.event_types.length > 0) {
    bits.push(`${spec.event_types.length} action${spec.event_types.length === 1 ? "" : "s"}`);
  }
  if (spec.actor_aliases && spec.actor_aliases.length > 0) {
    bits.push(`${spec.actor_aliases.length} actor${spec.actor_aliases.length === 1 ? "" : "s"}`);
  }
  if (spec.q && spec.q.length > 0) {
    bits.push(`search "${spec.q}"`);
  }
  return bits.length === 0 ? "all events" : bits.join(" · ");
}

/** Days inclusive between two yyyy-MM-dd dates. */
function daysBetween(from: string, to: string): number {
  if (!from || !to) return 0;
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
  return Math.round((b - a) / 86400000) + 1;
}

export function ExportModal(props: ExportModalProps): JSX.Element {
  const { open, onClose, onQueued, currentFilterSpec } = props;
  const navigate = useNavigate();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();

  const [include, setInclude] = useState<IncludeChoice>("filtered");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setInclude("filtered");
    setSubmitting(false);
    setError(null);
  }, [open]);

  const exportSpec: AuditExportFilterSpec = useMemo(() => {
    const from = currentFilterSpec.from ?? "";
    const to = currentFilterSpec.to ?? "";
    if (include === "all") {
      return { from, to, include: "all" };
    }
    const out: AuditExportFilterSpec = { from, to, include: "filtered" };
    if (currentFilterSpec.location_ids && currentFilterSpec.location_ids.length > 0) {
      out.locations = currentFilterSpec.location_ids;
    }
    if (currentFilterSpec.event_types && currentFilterSpec.event_types.length > 0) {
      out.event_types = currentFilterSpec.event_types;
    }
    if (currentFilterSpec.actor_aliases && currentFilterSpec.actor_aliases.length > 0) {
      out.actor_aliases = currentFilterSpec.actor_aliases;
    }
    if (currentFilterSpec.q) {
      out.q = currentFilterSpec.q;
    }
    return out;
  }, [include, currentFilterSpec]);

  const range = `${exportSpec.from} → ${exportSpec.to}`;
  const days = daysBetween(exportSpec.from, exportSpec.to);
  const tooLarge = days > 30;
  const filterSummary = describeFilters(exportSpec);

  const canSubmit = !submitting && !!exportSpec.from && !!exportSpec.to && !tooLarge;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await auditClient.createExport(exportSpec);
      toast.success({
        title: "Export queued",
        description: `Export ${resp.export_id.slice(0, 8)} for ${range}`,
      });
      onQueued(resp.export_id);
      onClose();
      navigate("/admin/audit/exports");
    } catch (err) {
      if (err instanceof ApiError) {
        const code =
          typeof err.body === "object" &&
          err.body !== null &&
          "error" in err.body
            ? String((err.body as { error: unknown }).error)
            : err.code ?? String(err.status);
        setError(code);
        handleApiError(err, { actionLabel: "Queue export" });
      } else {
        setError("unknown");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      title="Export audit log"
      variant="detail"
      footer={
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? "Queueing…" : "Queue export"}
          </Button>
        </>
      }
    >
      <div className="rb-audit__sub-modal">
        <label className="rb-audit__sub-label">What to include</label>
        <div
          className="rb-audit__sub-radio-row"
          role="radiogroup"
          aria-label="What to include"
        >
          <button
            type="button"
            role="radio"
            aria-checked={include === "filtered"}
            className={
              include === "filtered"
                ? "rb-audit__sub-radio rb-audit__sub-radio--active"
                : "rb-audit__sub-radio"
            }
            onClick={() => setInclude("filtered")}
            disabled={submitting}
          >
            Current filtered view
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={include === "all"}
            className={
              include === "all"
                ? "rb-audit__sub-radio rb-audit__sub-radio--active"
                : "rb-audit__sub-radio"
            }
            onClick={() => setInclude("all")}
            disabled={submitting}
          >
            All events in range
          </button>
        </div>

        <div className="rb-audit__sub-summary">
          Range <b>{range}</b> ({days} day{days === 1 ? "" : "s"}, max 30).
          <br />
          Filters: <span className="rb-audit__sub-summary-filters">{filterSummary}</span>
          <br />
          Output: <b>CSV</b> with one row per event. The Exports page will
          show a download link once the job is ready (typically &lt;30s for
          a 30-day window).
        </div>

        {tooLarge && (
          <div className="rb-audit__sub-error" role="alert">
            <code className="mono mono--sm">range_too_large</code> — pick a
            window of 30 days or fewer.
          </div>
        )}

        {error !== null && (
          <div className="rb-audit__sub-error" role="alert">
            <code className="mono mono--sm">{error}</code> — failed to queue.
          </div>
        )}
      </div>
    </Modal>
  );
}
